import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import * as React from 'react';
import { getDataAttributes } from '../../../../util/getDataAttributes/getDataAttributes.js';
import { extractMargins } from '../../../../util/spacing.js';
import '../../../../util/styles/index.js';
import { useLocaleContext } from '../../../../contexts/session/Locale.js';
import { usePlatformContext } from '../../../../contexts/session/Platform.js';
import { useThemeContext } from '../../../../contexts/session/Theme.js';
import { useEnvironmentContext } from '../../../../contexts/system/Environment.js';
import { useIconContext } from '../../../../contexts/system/Icon.js';
import { extractFlexItemProps } from '../../../../types/Props.js';
import { errorSvgDataUrl } from '../Icon.constants.js';
import { mapAriaAttributes, getIconData } from '../utility/Icon.helper.js';
import { useAdsStyles } from '../../../../util/styles/hooks/useAdsStyles.js';

var _excluded = ["iconId", "staticSize", "typographySize", "color", "showLoading"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var useData = function useData(data, color) {
  var _React$useState = React.useState(data.preloaded ? 'success' : 'pending'),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    state = _React$useState2[0],
    setState = _React$useState2[1];
  switch (state) {
    case 'pending':
      {
        return {
          url: data.url,
          onLoad: function onLoad() {
            return setState('success');
          },
          onLoadingError: function onLoadingError() {
            return setState('error');
          },
          loaded: false,
          fill: color,
          flip: data.flip
        };
      }
    case 'success':
      {
        return {
          url: data.url,
          onLoad: undefined,
          onLoadingError: undefined,
          loaded: true,
          fill: color,
          flip: data.flip
        };
      }
    case 'error':
      {
        return {
          url: errorSvgDataUrl,
          onLoad: undefined,
          onLoadingError: undefined,
          loaded: true,
          fill: color,
          flip: false
        };
      }
    default:
      {
        (function (_) {})(state);
        throw new Error("Invalid icon loading state: ".concat(state));
      }
  }
};
var useInlineContainer = function useInlineContainer(typographySize, setIconSize) {
  if (!typographySize) {
    return undefined;
  }
  return {
    variant: typographySize,
    setContentSize: setIconSize
  };
};
var useLoadingContainer = function useLoadingContainer(data, showLoading, iconSize) {
  if (!showLoading || data.loaded) {
    return undefined;
  }
  return {
    iconSize: iconSize
  };
};
var useIconContent = function useIconContent(data, iconSize) {
  return {
    iconSize: iconSize,
    url: data.url,
    loaded: data.loaded,
    onLoad: data.onLoad,
    onLoadingError: data.onLoadingError,
    flip: data.flip,
    fill: data.fill
  };
};
var defaults = {
  color: 'base.default',
  showLoading: false
};
var useIcon = function useIcon(_ref) {
  var iconId = _ref.iconId,
    staticSize = _ref.staticSize,
    typographySize = _ref.typographySize,
    _ref$color = _ref.color,
    color = _ref$color === void 0 ? defaults.color : _ref$color,
    _ref$showLoading = _ref.showLoading,
    showLoading = _ref$showLoading === void 0 ? defaults.showLoading : _ref$showLoading,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _extractMargins = extractMargins(rest),
    marginProps = _extractMargins.margins,
    marginsRest = _extractMargins.rest;
  var _extractFlexItemProps = extractFlexItemProps(marginsRest),
    flexItemProps = _extractFlexItemProps.flexItemProps,
    flexItemRest = _extractFlexItemProps.rest;
  var _getDataAttributes = getDataAttributes(flexItemRest),
    dataAttributes = _getDataAttributes.dataAttributes,
    dataRest = _getDataAttributes.rest;
  var _mapAriaAttributes = mapAriaAttributes(dataRest),
    ariaAttributes = _mapAriaAttributes.ariaAttributes;
  var environment = useEnvironmentContext();
  var localIcons = useIconContext();
  var _useLocaleContext = useLocaleContext(),
    direction = _useLocaleContext.direction,
    locale = _useLocaleContext.locale;
  var _usePlatformContext = usePlatformContext(),
    platform = _usePlatformContext.platform;
  var theme = useThemeContext();
  var _useAdsStyles = useAdsStyles(),
    mapAdsStyles = _useAdsStyles.mapAdsStyles;
  var _React$useState3 = React.useState(),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    actualTypographySize = _React$useState4[0],
    setActualTypographySize = _React$useState4[1];
  var iconSize = typographySize && actualTypographySize || staticSize && theme.icons[staticSize] || '0px';
  var iconData = getIconData(iconId, {
    localIcons: localIcons,
    direction: direction,
    locale: locale,
    platform: platform,
    theme: theme.name,
    cdnUrl: environment.icon.cdnUrl
  });
  var data = useData(iconData, color);
  var componentContainerStyle = _objectSpread(_objectSpread(_objectSpread({}, dataAttributes), ariaAttributes), mapAdsStyles(_objectSpread(_objectSpread({
    display: 'inline-flex'
  }, marginProps), flexItemProps)));
  var inlineContainerProps = useInlineContainer(typographySize, setActualTypographySize);
  var loadingContainerProps = useLoadingContainer(data, showLoading, iconSize);
  var iconContentProps = useIconContent(data, iconSize);
  return _objectSpread({
    componentContainerStyle: componentContainerStyle,
    inlineContainerProps: inlineContainerProps,
    loadingContainerProps: loadingContainerProps,
    iconContentProps: iconContentProps
  }, data);
};

export { useIcon };
//# sourceMappingURL=useIcon.js.map
